import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Button, Row, Col, InputGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { addCustomer, updateCustomer } from "../../redux/features/customer.features";
import { toast } from "react-toastify";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaBuilding,
  FaImage,
  FaFingerprint,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaIdCard,
  FaCheckCircle,
  FaTruck,
  FaBriefcase,
  FaShieldAlt,
} from "react-icons/fa";

const CustomerSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().nullable(),
  phone_number: Yup.string()
    .matches(/^[0-9+\- ]+$/, "Invalid phone number format")
    .min(9, "Phone number is too short")
    .required("Primary phone is required"),
  email: Yup.string().email("Invalid email format").nullable(),
  customer_type: Yup.string().oneOf(["Individual", "Business"], "Invalid Type").required("Customer type is required"),
  company_name: Yup.string().nullable(),
  customer_image: Yup.string().nullable(),
  company_tax_id: Yup.string().nullable(),
  alternate_phone: Yup.string().nullable(),
  address: Yup.string().nullable(),
  address2: Yup.string().nullable(),
  city: Yup.string().nullable(),
  state: Yup.string().nullable(),
  zip: Yup.string().nullable(),
  country: Yup.string().nullable(),
  shipping_notes: Yup.string().nullable(),
  creditLimit: Yup.number().typeError("Must be a number").min(0, "Cannot be negative").nullable(),
  nic_number: Yup.string().nullable(),
  id_front_image: Yup.string().url("Invalid URL").nullable(),
  id_back_image: Yup.string().url("Invalid URL").nullable(),
  customer_tier: Yup.string().oneOf(["Retail", "B2B"]).default("Retail"),
  b2b_credit_limit: Yup.number().typeError("Must be a number").min(0).nullable(),
});

const CustomerForm = ({ setShow, handleClose, savedCompleted, selectedCustomer, isEditMode, customerId }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("general"); // "general" | "shipping" | "business" | "kyc"
  const [localCustomer, setLocalCustomer] = useState(selectedCustomer);

  useEffect(() => {
    setLocalCustomer(selectedCustomer);
  }, [selectedCustomer]);

  const customers = useSelector((state) => state.customer?.customers || []);
  const foundCustomer = customerId && !selectedCustomer ? customers.find((c) => c._id === customerId) : null;
  const activeCustomer = localCustomer || foundCustomer || selectedCustomer;

  const closeForm = () => {
    if (typeof setShow === "function") setShow(false);
    if (typeof handleClose === "function") handleClose();
  };

  const initialValues = {
    _id: activeCustomer ? activeCustomer._id : "",
    customer_id: activeCustomer ? activeCustomer.customer_id : "",
    first_name: activeCustomer ? activeCustomer.first_name : "",
    last_name: activeCustomer ? activeCustomer.last_name || "" : "",
    email: activeCustomer ? activeCustomer.email || "" : "",
    phone_number: activeCustomer ? activeCustomer.phone_number : "",
    alternate_phone: activeCustomer ? activeCustomer.alternate_phone || "" : "",
    customer_type: activeCustomer ? activeCustomer.customer_type || "Individual" : "Individual",
    customer_tier: activeCustomer ? activeCustomer.customer_tier || "Retail" : "Retail",
    b2b_credit_limit: activeCustomer ? activeCustomer.b2b_credit_limit || 0 : 0,
    company_name: activeCustomer ? activeCustomer.company_name || "" : "",
    customer_image: activeCustomer ? activeCustomer.customer_image || "" : "",
    company_tax_id: activeCustomer ? activeCustomer.company_tax_id || "" : "",
    address: activeCustomer ? activeCustomer.address || "" : "",
    address2: activeCustomer ? activeCustomer.address2 || "" : "",
    city: activeCustomer ? activeCustomer.city || "" : "",
    state: activeCustomer ? activeCustomer.state || "" : "",
    zip: activeCustomer ? activeCustomer.zip || "" : "",
    country: activeCustomer ? activeCustomer.country || "Sri Lanka" : "Sri Lanka",
    shipping_notes: activeCustomer ? activeCustomer.shipping_notes || "" : "",
    creditLimit: activeCustomer ? activeCustomer.creditLimit || 0 : 0,
    nic_number: activeCustomer ? activeCustomer.nic_number || "" : "",
    id_front_image: activeCustomer ? activeCustomer.id_front_image || "" : "",
    id_back_image: activeCustomer ? activeCustomer.id_back_image || "" : "",
  };

  const [conflictData, setConflictData] = useState(null);

  const isSystemWalkIn = isEditMode && (
    activeCustomer?.isWalkIn === true ||
    activeCustomer?.isSystemProtected === true ||
    activeCustomer?.customer_id === "CUS-000001" ||
    activeCustomer?.customer_id === "CUS-WALKIN" ||
    activeCustomer?.email === "walkin@store.local" ||
    activeCustomer?.phone_number === "0000000000"
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    setConflictData(null);
    if (isSystemWalkIn) {
      toast.warning("System default Walk-In Customer cannot be edited.");
      setSubmitting(false);
      return;
    }
    try {
      let result = null;
      if (isEditMode) {
        result = await dispatch(updateCustomer(values)).unwrap();
        toast.success("Customer profile updated successfully");
      } else {
        result = await dispatch(addCustomer(values)).unwrap();
        toast.success("New customer registered successfully");
      }
      if (savedCompleted) savedCompleted(result);
      closeForm();
    } catch (error) {
      if (error?.status === 409 || error?.code === 409 || error?.message?.includes("already registered")) {
        setConflictData(error.existingCustomer || true);
        toast.warning("This phone number is already registered.");
      } else {
        toast.error(error?.message || `Failed to ${isEditMode ? "update" : "register"} customer`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-100 h-100 bg-white overflow-hidden">
      {/* Header bar */}
      <div className="px-4 py-3 bg-slate-900 text-white d-flex align-items-center justify-content-between border-bottom" style={{ backgroundColor: "#0f172a" }}>
        <div className="d-flex align-items-center gap-2">
          <div className="d-inline-flex align-items-center justify-content-center bg-primary rounded-2" style={{ width: 28, height: 28 }}>
            <FaUser className="text-white" size={13} />
          </div>
          <div>
            <h6 className="mb-0 fw-bold text-white fs-6">
              {isEditMode ? `Edit Customer: ${activeCustomer?.first_name || ""} ${activeCustomer?.last_name || ""}` : "Register New Customer"}
            </h6>
            {activeCustomer?.customer_id && (
              <span className="font-monospace text-slate-400 small" style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                ID: {activeCustomer.customer_id}
              </span>
            )}
          </div>
        </div>
      </div>

      <Formik initialValues={initialValues} validationSchema={CustomerSchema} enableReinitialize onSubmit={handleSubmit}>
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            {/* Segmented Tab Navigation Header */}
            <div className="d-flex border-bottom bg-light px-3 pt-2 gap-2" style={{ backgroundColor: "#f8fafc", borderBottomColor: "#e2e8f0" }}>
              <button
                type="button"
                onClick={() => setActiveTab("general")}
                className={`btn btn-sm d-flex align-items-center gap-2 border-0 rounded-top pb-2 px-3 fw-semibold ${
                  activeTab === "general"
                    ? "bg-white text-primary border-bottom border-primary border-2 shadow-xs"
                    : "text-muted hover:text-dark"
                }`}
                style={{
                  borderBottom: activeTab === "general" ? "2px solid #2563eb" : "2px solid transparent",
                  backgroundColor: activeTab === "general" ? "#ffffff" : "transparent",
                  fontSize: "0.82rem",
                }}
              >
                <FaUser size={12} />
                Profile & Contact
                {touched.first_name && errors.first_name && <span className="badge bg-danger rounded-pill p-1" style={{ width: 6, height: 6 }} />}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("shipping")}
                className={`btn btn-sm d-flex align-items-center gap-2 border-0 rounded-top pb-2 px-3 fw-semibold ${
                  activeTab === "shipping"
                    ? "bg-white text-primary border-bottom border-primary border-2 shadow-xs"
                    : "text-muted hover:text-dark"
                }`}
                style={{
                  borderBottom: activeTab === "shipping" ? "2px solid #2563eb" : "2px solid transparent",
                  backgroundColor: activeTab === "shipping" ? "#ffffff" : "transparent",
                  fontSize: "0.82rem",
                }}
              >
                <FaTruck size={12} />
                Shipping & Address
                {values.city && <span className="badge bg-primary-subtle text-primary x-small px-1.5 py-0.5 rounded">{values.city}</span>}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("business")}
                className={`btn btn-sm d-flex align-items-center gap-2 border-0 rounded-top pb-2 px-3 fw-semibold ${
                  activeTab === "business"
                    ? "bg-white text-primary border-bottom border-primary border-2 shadow-xs"
                    : "text-muted hover:text-dark"
                }`}
                style={{
                  borderBottom: activeTab === "business" ? "2px solid #2563eb" : "2px solid transparent",
                  backgroundColor: activeTab === "business" ? "#ffffff" : "transparent",
                  fontSize: "0.82rem",
                }}
              >
                <FaBriefcase size={12} />
                Business & Credit
                {values.customer_type === "Business" && <span className="badge bg-info text-dark x-small px-1.5 py-0.5 rounded">Business</span>}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("kyc")}
                className={`btn btn-sm d-flex align-items-center gap-2 border-0 rounded-top pb-2 px-3 fw-semibold ${
                  activeTab === "kyc"
                    ? "bg-white text-primary border-bottom border-primary border-2 shadow-xs"
                    : "text-muted hover:text-dark"
                }`}
                style={{
                  borderBottom: activeTab === "kyc" ? "2px solid #2563eb" : "2px solid transparent",
                  backgroundColor: activeTab === "kyc" ? "#ffffff" : "transparent",
                  fontSize: "0.82rem",
                }}
              >
                <FaShieldAlt size={12} />
                Identity (KYC)
                {values.nic_number && <FaCheckCircle size={10} className="text-success" />}
              </button>
            </div>

            {/* Form Body */}
            <div className="p-4" style={{ minHeight: "330px" }}>
              {conflictData && (
                <div className="alert alert-warning py-2 px-3 border-warning d-flex align-items-center mb-3 text-dark small rounded-2">
                  <FaFingerprint size={18} className="text-warning me-2 shrink-0" />
                  <div>
                    <strong>Duplicate Number:</strong> The phone <code>{values.phone_number}</code> belongs to{" "}
                    <strong>{conflictData.name || "Existing Customer"}</strong> ({conflictData.customer_id || "ID"}).
                  </div>
                </div>
              )}

              {/* TAB 1: Profile & Contact */}
              {activeTab === "general" && (
                <div className="animate__animated animate__fadeIn animate__faster">
                  <Row className="g-3">
                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        First Name <span className="text-danger">*</span>
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaUser size={12} />
                        </InputGroup.Text>
                        <Field
                          name="first_name"
                          className={`form-control border-start-0 py-1.5 ${touched.first_name && errors.first_name ? "is-invalid" : ""}`}
                          placeholder="e.g. Ruwan"
                        />
                      </InputGroup>
                      {touched.first_name && errors.first_name && <div className="text-danger x-small mt-0.5">{errors.first_name}</div>}
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Last Name
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaUser size={12} />
                        </InputGroup.Text>
                        <Field name="last_name" className="form-control border-start-0 py-1.5" placeholder="e.g. Perera" />
                      </InputGroup>
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Primary Phone <span className="text-danger">*</span>
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-success">
                          <FaPhoneAlt size={12} />
                        </InputGroup.Text>
                        <Field
                          name="phone_number"
                          className={`form-control border-start-0 py-1.5 ${touched.phone_number && errors.phone_number ? "is-invalid" : ""}`}
                          placeholder="077 123 4567"
                        />
                      </InputGroup>
                      {touched.phone_number && errors.phone_number && <div className="text-danger x-small mt-0.5">{errors.phone_number}</div>}
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Alternative / WhatsApp
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaPhoneAlt size={12} />
                        </InputGroup.Text>
                        <Field name="alternate_phone" className="form-control border-start-0 py-1.5" placeholder="071 987 6543 (Optional)" />
                      </InputGroup>
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Email Address
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaEnvelope size={12} />
                        </InputGroup.Text>
                        <Field
                          name="email"
                          type="email"
                          className={`form-control border-start-0 py-1.5 ${touched.email && errors.email ? "is-invalid" : ""}`}
                          placeholder="customer@email.com"
                        />
                      </InputGroup>
                      {touched.email && errors.email && <div className="text-danger x-small mt-0.5">{errors.email}</div>}
                    </Col>

                    <Col md={3}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Customer Type
                      </label>
                      <div className="btn-group w-100 btn-group-sm" role="group">
                        <button
                          type="button"
                          onClick={() => setFieldValue("customer_type", "Individual")}
                          className={`btn btn-sm ${values.customer_type === "Individual" ? "btn-primary fw-bold" : "btn-outline-secondary"}`}
                        >
                          Individual
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFieldValue("customer_type", "Business");
                          }}
                          className={`btn btn-sm ${values.customer_type === "Business" ? "btn-primary fw-bold" : "btn-outline-secondary"}`}
                        >
                          Business
                        </button>
                      </div>
                    </Col>

                    <Col md={3}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Pricing Tier
                      </label>
                      <div className="btn-group w-100 btn-group-sm" role="group">
                        <button
                          type="button"
                          onClick={() => setFieldValue("customer_tier", "Retail")}
                          className={`btn btn-sm ${values.customer_tier === "Retail" ? "btn-dark fw-bold" : "btn-outline-secondary"}`}
                        >
                          Retail
                        </button>
                        <button
                          type="button"
                          onClick={() => setFieldValue("customer_tier", "B2B")}
                          className={`btn btn-sm ${values.customer_tier === "B2B" ? "btn-dark fw-bold" : "btn-outline-secondary"}`}
                        >
                          B2B
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* TAB 2: Shipping & Address */}
              {activeTab === "shipping" && (
                <div className="animate__animated animate__fadeIn animate__faster">
                  <Row className="g-3">
                    <Col md={8}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Street Address (Line 1)
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaMapMarkerAlt size={12} />
                        </InputGroup.Text>
                        <Field name="address" className="form-control border-start-0 py-1.5" placeholder="e.g. No. 142, 1st Cross Street" />
                      </InputGroup>
                    </Col>

                    <Col md={4}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Apartment / Suite / Line 2
                      </label>
                      <Field name="address2" className="form-control form-control-sm py-1.5" placeholder="e.g. Shop #2, Ground Floor" />
                    </Col>

                    <Col md={4}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        City / Town
                      </label>
                      <Field name="city" className="form-control form-control-sm py-1.5" placeholder="e.g. Pettah, Colombo" />
                    </Col>

                    <Col md={4}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        District / Province
                      </label>
                      <Field name="state" className="form-control form-control-sm py-1.5" placeholder="e.g. Western Province" />
                    </Col>

                    <Col md={2}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Postal Code
                      </label>
                      <Field name="zip" className="form-control form-control-sm py-1.5 font-monospace" placeholder="01100" />
                    </Col>

                    <Col md={2}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Country
                      </label>
                      <Field name="country" className="form-control form-control-sm py-1.5" placeholder="Sri Lanka" />
                    </Col>

                    <Col md={12}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Courier & Delivery Instructions
                      </label>
                      <Field
                        as="textarea"
                        rows={2}
                        name="shipping_notes"
                        className="form-control form-control-sm"
                        placeholder="e.g. Call customer before delivery, cash on delivery only, deliver between 10am - 4pm"
                      />
                    </Col>
                  </Row>
                </div>
              )}

              {/* TAB 3: Business & Credit */}
              {activeTab === "business" && (
                <div className="animate__animated animate__fadeIn animate__faster">
                  <Row className="g-3">
                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Registered Company Name
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaBuilding size={12} />
                        </InputGroup.Text>
                        <Field name="company_name" className="form-control border-start-0 py-1.5" placeholder="e.g. Apex Cellular PVT LTD" />
                      </InputGroup>
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Business Registration / Tax ID (BRN)
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaFingerprint size={12} />
                        </InputGroup.Text>
                        <Field name="company_tax_id" className="form-control border-start-0 py-1.5 font-monospace" placeholder="PV-102938" />
                      </InputGroup>
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Retail Credit Limit (LKR)
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaMoneyBillWave size={12} />
                        </InputGroup.Text>
                        <Field name="creditLimit" type="number" className="form-control border-start-0 py-1.5" placeholder="0.00" />
                      </InputGroup>
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        B2B Wholesale Credit Limit (LKR)
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaMoneyBillWave size={12} />
                        </InputGroup.Text>
                        <Field name="b2b_credit_limit" type="number" className="form-control border-start-0 py-1.5" placeholder="0.00" />
                      </InputGroup>
                    </Col>

                    <Col md={12}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        Company Logo / Image URL
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaImage size={12} />
                        </InputGroup.Text>
                        <Field name="customer_image" className="form-control border-start-0 py-1.5" placeholder="https://..." />
                      </InputGroup>
                    </Col>
                  </Row>
                </div>
              )}

              {/* TAB 4: KYC & Legal */}
              {activeTab === "kyc" && (
                <div className="animate__animated animate__fadeIn animate__faster">
                  <Row className="g-3">
                    <Col md={12}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        National Identity Card (NIC) / Passport No
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaIdCard size={12} />
                        </InputGroup.Text>
                        <Field name="nic_number" className="form-control border-start-0 py-1.5 font-monospace" placeholder="199XXXXXXXXX / XXXXXXXXV" />
                      </InputGroup>
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        ID Document Front (Image URL)
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaImage size={12} />
                        </InputGroup.Text>
                        <Field name="id_front_image" className="form-control border-start-0 py-1.5" placeholder="https://..." />
                      </InputGroup>
                    </Col>

                    <Col md={6}>
                      <label className="form-label text-muted fw-semibold x-small mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                        ID Document Back (Image URL)
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0 text-muted">
                          <FaImage size={12} />
                        </InputGroup.Text>
                        <Field name="id_back_image" className="form-control border-start-0 py-1.5" placeholder="https://..." />
                      </InputGroup>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            {/* Footer Action Bar */}
            <div className="px-4 py-3 bg-light border-top d-flex align-items-center justify-content-between" style={{ backgroundColor: "#f8fafc", borderTopColor: "#e2e8f0" }}>
              <div className="text-muted small">
                {activeTab === "general" && <span className="x-small">Required: First Name & Primary Phone</span>}
                {activeTab === "shipping" && <span className="x-small">Used for Courier & POS Shipping Labels</span>}
                {activeTab === "business" && <span className="x-small">Applicable for Wholesale & B2B accounts</span>}
                {activeTab === "kyc" && <span className="x-small">Optional identity & verification records</span>}
              </div>

              <div className="d-flex gap-2">
                <Button variant="light" size="sm" onClick={closeForm} disabled={isSubmitting} className="px-3 border fw-semibold text-secondary">
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={isSubmitting} className="px-4 fw-bold shadow-xs">
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1.5" role="status" aria-hidden="true" />
                      Saving...
                    </>
                  ) : (
                    <>{isEditMode ? "Update Customer" : "Save Customer"}</>
                  )}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CustomerForm;